/**
 * Answers of path tasks — computed from the RULE, never typed as squares only (docs/PATH-V2.md §6.2–6.3,
 * docs/path-v2/CONTRACTS.md §3.2). One definition of a gift: `analysis/gifts.ts` (§12.3).
 *
 * - `answerOf(task)` — the full truth of a task (every target square / every move meeting the goal / yes-no / the
 *   option number).
 * - `checkTap`, `checkMove`, `checkYesNo`, `checkChoose` — judge one answer of the child.
 * - `classifyMistake(task, answer)` — the reason line for a wrong answer (§6.3), `task.why` first.
 * - `consequenceOf(fen, uci)` — the opponent's reply that shows WHY a move is bad (the red arrow, §3.4 p.13).
 * - `verifyTask(task)` — truth problems of authored content (`pnpm path:report`); a trade task (ch. 1.4) also wants
 *   its count on the square to agree with the board (`tradeProblem`).
 *
 * Pure and isomorphic.
 */
import type { ChooseRule, Color, MistakeReason, MoveRule, Square, TapRule, YesNoRule } from '@gambit/shared';
import { PATH_PIECE_VALUE } from '@gambit/shared';
import type { TaskSpec } from '@gambit/content';
import { attackersOf, fileOf, opposite, parsePlacement, rankOf, seeCapture, seeLoss, squareIndex, squareName, VALUE_PAWNS, type BoardPiece } from '../analysis/board.ts';
import { captureNet, giftMoves, guards, isGift, isSafeMove, reaches } from '../analysis/gifts.ts';
import {
  captureOf,
  isCheck,
  isLegalMove,
  isMate,
  legalMovesUci,
  normalizeUci,
  pieceAt,
  positionProblem,
  sideToMove,
  tryApplyMove,
  withTurn,
} from './kingless.ts';

export type YesNo = 'yes' | 'no';
export type ChoiceN = 1 | 2 | 3;

/** The truth of a task. */
export type TaskAnswer =
  | { kind: 'show' }
  /** every square to touch (find-all: all of them) */
  | { kind: 'tap'; squares: Square[] }
  /** every legal move meeting the goal (UCI, generated spelling) */
  | { kind: 'move'; moves: string[] }
  | { kind: 'yesno'; answer: YesNo }
  /** the option meeting the rule (null = none or several — see `matching`) */
  | { kind: 'choose'; answer: ChoiceN | null; matching: ChoiceN[] }
  /** 👎 on the bad move, then any of `moves` */
  | { kind: 'teachme'; judge: YesNo; moves: string[] }
  /** flags to collect with the piece on `from` */
  | { kind: 'flags'; from: Square; squares: Square[] };

// ───────────────────────── rule truth ─────────────────────────

/** Target squares of gift captures (poison-checked `isGift`; en passant → the passed pawn's square). */
function giftTargetSquares(fen: string): Square[] {
  const out = new Set<Square>();
  for (const uci of giftMoves(fen)) {
    const cap = captureOf(fen, uci);
    if (cap) out.add(cap.square);
  }
  return [...out].sort((a, b) => squareIndex(a) - squareIndex(b));
}

/**
 * «Что он хочет?» (v1): squares of pieces of the side to move that the opponent piece on `from` wins as a gift
 * (`isGift` with the opponent put to move). [] when `from` holds no opponent piece.
 */
export function threatSquares(fen: string, from: Square): Square[] {
  const piece = pieceAt(fen, from);
  if (!piece || piece.color === sideToMove(fen)) return [];
  const out = new Set<Square>();
  for (const uci of giftMoves(withTurn(fen, piece.color))) if (uci.slice(0, 2) === from) out.add(uci.slice(2, 4));
  return [...out].sort((a, b) => squareIndex(a) - squareIndex(b));
}

/**
 * The static exchange of the capture `uci` for the mover, in pawns (1/3/3/5/9): what it takes minus what the
 * recaptures on that square give back (`analysis/board.ts` SEE — the same exchange `saves` counts). Null for an
 * illegal or quiet move.
 */
export function tradeBalance(fen: string, uci: string): number | null {
  const norm = normalizeUci(fen, uci);
  if (norm === null) return null;
  const board = parsePlacement(fen);
  // what it takes, read off the board (one move generation less than `captureOf`): a legal move onto the enemy, or en
  // passant — the passed pawn is taken from its own square
  const taken = takenOn(board, norm);
  const victim = board[squareIndex(taken)];
  const mover = board[squareIndex(norm.slice(0, 2))];
  if (!victim || !mover || victim.color === mover.color) return null;
  const to = squareIndex(norm.slice(2, 4));
  const ep = taken !== norm.slice(2, 4);
  if (ep) board[squareIndex(taken)] = null;
  return (seeCapture(board, squareIndex(norm.slice(0, 2)), to) + (ep ? VALUE_PAWNS.p * 100 : 0)) / 100;
}

/** «Бери, если забираешь не дешевле, чем отдаёшь» (ch. 1.4): the capture takes no less than it gives — a gift too. */
function goodTrade(fen: string, uci: string): boolean {
  const balance = tradeBalance(fen, uci);
  return balance !== null && balance >= 0;
}

/**
 * A trade question is sound when its count on the square (`tradeBalance`) and the board-wide search
 * (`analysis/gifts.ts` `captureNet`: checks, mates, other captures) agree on it — nothing else on the board turns
 * the trade, so the child's «что забираем, что отдаём» is the whole truth. The problem, or null.
 */
export function tradeProblem(fen: string, uci: string): string | null {
  const count = tradeBalance(fen, uci);
  if (count === null) return `${uci} is not a capture`;
  const net = captureNet(fen, uci) as number;
  return (count >= 0) === (net >= 0) ? null : `${uci}: the board turns the trade (count ${count}, board ${net})`;
}

/** Squares a tap task asks for. */
export function tapTargets(fen: string, rule: TapRule): Square[] {
  switch (rule.rule) {
    case 'gift':
      return giftTargetSquares(fen);
    case 'reaches':
      return reaches(fen, rule.from, { targets: true });
    case 'guards':
      return guards(fen, rule.square);
    case 'threat':
      return threatSquares(fen, rule.from);
    case 'squares':
      return [...rule.squares];
  }
}

/** The square the piece of `square` stands on after `uci` (it moved / it stayed). */
function followPiece(square: Square, uci: string): Square {
  return uci.slice(0, 2) === square ? uci.slice(2, 4) : square;
}

/** Can the opponent (to move in `after`) take the piece on `sq` as a gift (an `isGift` capture of it)? */
function pieceGiven(after: string, sq: Square): boolean {
  const board = parsePlacement(after);
  return giftMoves(after).some((m) => takenOn(board, m) === sq);
}

/**
 * The square a legal capture `m` takes on, read off the board it is played on: the landing square, or — en passant (a
 * pawn stepping sideways onto an empty square) — the passed pawn's own. (`captureOf` per move costs a move generation;
 * these run over every reply of every board a generator tries.)
 */
function takenOn(board: readonly (BoardPiece | null)[], m: string): Square {
  const p = board[squareIndex(m.slice(0, 2))];
  return (p?.type === 'p' && m.charAt(0) !== m.charAt(2) && board[squareIndex(m.slice(2, 4))] == null ? `${m.charAt(2)}${m.charAt(1)}` : m.slice(2, 4)) as Square;
}

/** Can the opponent (to move in `after`) win the piece on `sq`: an `isGift` capture of it or a losing exchange. */
function pieceLost(after: string, sq: Square): boolean {
  if (pieceGiven(after, sq)) return true;
  const board = parsePlacement(after);
  const idx = squareIndex(sq);
  if (idx < 0 || !board[idx]) return true; // the piece is gone
  return seeLoss(board, idx) > 0;
}

/**
 * Squares of the pieces (no king) of the side NOT to move in `pos` that the side to move wins there: an `isGift`
 * capture or a losing exchange (SEE) — `pieceLost` over every such piece.
 */
function lostSquaresIn(pos: string): Set<Square> {
  const out = new Set<Square>();
  const board = parsePlacement(pos);
  for (const m of giftMoves(pos)) out.add(takenOn(board, m));
  const victim = opposite(sideToMove(pos));
  board.forEach((p, i) => {
    if (p !== null && p.color === victim && p.type !== 'k' && seeLoss(board, i) > 0) out.add(squareName(i));
  });
  return out;
}

/** One-entry memo of «what was lost before» (`movesMeeting` asks it once per legal move of the same position). */
let lostBeforeMemo: { fen: string; lost: ReadonlySet<Square> } | null = null;

/**
 * The own pieces the side to move has lost ALREADY in `fen` (the opponent, put to move, wins them: `lostSquaresIn`).
 * In check there is no such view: nothing counts as lost before (`isSafeMove`'s «already a gift» rule).
 */
function lostBefore(fen: string): ReadonlySet<Square> {
  if (lostBeforeMemo?.fen === fen) return lostBeforeMemo.lost;
  const lost: ReadonlySet<Square> = isCheck(fen) ? new Set<Square>() : lostSquaresIn(withTurn(fen, opposite(sideToMove(fen))));
  lostBeforeMemo = { fen, lost };
  return lost;
}

/**
 * «Nothing of ours is lost» — ONE notion for `saves` and `escapesCheck` (docs/path-v2/CONTRACTS.md v1-H): the squares
 * of own pieces (no king) the opponent wins after the legal move `uci` (`pieceLost`: an `isGift` capture or a losing
 * exchange) that were not lost already before it — the same piece on the same square, lost with the opponent put to
 * move (`isSafeMove`'s «already a gift», widened to exchanges). The moved piece is always judged afresh (it stands
 * on a new square); in check nothing counts as lost before, so an escape loses nothing at all.
 */
export function newLosses(fen: string, uci: string, after: string): Square[] {
  const now = lostSquaresIn(after);
  if (now.size === 0) return [];
  const before = lostBefore(fen);
  const boardBefore = parsePlacement(fen);
  const boardAfter = parsePlacement(after);
  const us = sideToMove(fen);
  const stayed = (sq: Square): boolean => {
    const a = boardBefore[squareIndex(sq)];
    const b = boardAfter[squareIndex(sq)];
    // an own piece can only land on an empty or an enemy square: the same own piece there stood still
    return a !== null && a !== undefined && b !== null && b !== undefined && a.color === us && a.type === b.type && a.color === b.color;
  };
  return [...now].filter((sq) => !(before.has(sq) && stayed(sq))).sort((a, b) => squareIndex(a) - squareIndex(b));
}

/** Squares of the pieces giving check in `after` (the side to move there is the one in check). */
export function checkersIn(after: string): Square[] {
  const board = parsePlacement(after);
  const us = sideToMove(after);
  const king = board.findIndex((p) => p?.type === 'k' && p.color === us);
  return king < 0 ? [] : attackersOf(board, king, opposite(us)).map(squareName);
}

/**
 * A SAFE check — ONE definition for the move rule `safeCheck` (lessons, `safeCheck@1`) and the mini-game «Первый шах»
 * (§6.6 «засчитывается только безопасный шах»): the legal move `uci` gives check, leaves nothing newly en prise
 * (`isSafeMove`), and no piece giving check is an `isGift` capture of the opponent after it — a discovered check
 * whose rook the knight takes for free is no safe check, even when the rook was hanging before.
 */
export function isSafeCheck(fen: string, uci: string): boolean {
  const after = tryApplyMove(fen, uci);
  if (after === null || !isCheck(after)) return false;
  if (!isSafeMove(fen, uci)) return false;
  const checkers = new Set(checkersIn(after));
  return !giftMoves(after).some((g) => checkers.has(g.slice(2, 4)));
}

/** Does the legal move `uci` meet `rule`? (`uci` must be legal in `fen`.) */
function meetsMoveRule(fen: string, uci: string, rule: MoveRule): boolean {
  switch (rule.rule) {
    case 'takesGift':
      return isGift(fen, uci);
    case 'mate': {
      const after = tryApplyMove(fen, uci);
      return after !== null && isMate(after);
    }
    case 'saves': {
      // v1-H: the piece «под ударом» (followed to its new square) is no longer lost AND nothing else of ours is newly
      // lost — a «save» that drops another piece (a guard walks off, a block by a piece that is then taken) is none
      const own = pieceAt(fen, rule.square);
      if (!own || own.color !== sideToMove(fen)) return false;
      const after = tryApplyMove(fen, uci);
      if (after === null) return false;
      return !pieceLost(after, followPiece(rule.square, uci)) && newLosses(fen, uci, after).length === 0;
    }
    case 'safeCheck':
      return isSafeCheck(fen, uci);
    case 'escapesCheck': {
      // the contract: after the answer NO own piece is lost — not the moved one only (a king step that leaves the
      // queen to an exchange is no escape); in check nothing is «lost before», so `newLosses` is every loss
      if (!isCheck(fen)) return false;
      const after = tryApplyMove(fen, uci);
      return after !== null && newLosses(fen, uci, after).length === 0;
    }
    case 'goodTrade':
      return goodTrade(fen, uci);
    case 'uci': {
      const norm = normalizeUci(fen, uci);
      return rule.moves.some((m) => normalizeUci(fen, m) === norm);
    }
  }
}

/** Every legal move meeting `rule`. */
export function movesMeeting(fen: string, rule: MoveRule): string[] {
  // explicit moves: compared with the generated spelling, normalised only when spelt otherwise (once, not per move)
  if (rule.rule === 'uci') {
    const legal = legalMovesUci(fen);
    const spelt = new Set(legal);
    const want = new Set(rule.moves.map((m) => (spelt.has(m) ? m : normalizeUci(fen, m))));
    return legal.filter((m) => want.has(m));
  }
  return legalMovesUci(fen).filter((m) => meetsMoveRule(fen, m, rule));
}

/** Truth of a 👍/👎 question about `move`. */
export function yesNoTruth(fen: string, rule: YesNoRule): YesNo {
  // the opponent's last move is already on the board: the question is about the piece it moved
  if (rule.rule === 'threat') return threatSquares(fen, rule.move.slice(2, 4)).length > 0 ? 'yes' : 'no';
  if (!isLegalMove(fen, rule.move)) return 'no';
  switch (rule.rule) {
    case 'isGift':
      return isGift(fen, rule.move) ? 'yes' : 'no';
    case 'isSafe':
      return isSafeMove(fen, rule.move) ? 'yes' : 'no';
    case 'isMate': {
      const after = tryApplyMove(fen, rule.move);
      return after !== null && isMate(after) ? 'yes' : 'no';
    }
    case 'goodTrade':
      return goodTrade(fen, rule.move) ? 'yes' : 'no';
  }
}

/** Options (1-based) of a choose task that meet its rule: per option, or the most valuable capture. */
function chooseMatching(fen: string, rule: ChooseRule, options: readonly string[]): ChoiceN[] {
  if (rule.rule === 'mostValuable') {
    // an illegal option is worth -1, a quiet one 0
    const valueOf = (m: string): number => {
      if (!isLegalMove(fen, m)) return -1;
      const cap = captureOf(fen, m);
      return cap ? PATH_PIECE_VALUE[cap.piece.type] : 0;
    };
    const values = options.map(valueOf);
    const max = Math.max(0, ...values);
    return max > 0 ? values.flatMap((v, i) => (v === max ? [(i + 1) as ChoiceN] : [])) : [];
  }
  const r = rule.rule;
  return options.flatMap((move, i) => (yesNoTruth(fen, { rule: r, move }) === 'yes' ? [(i + 1) as ChoiceN] : []));
}

/** The full truth of a task. */
export function answerOf(task: TaskSpec): TaskAnswer {
  switch (task.kind) {
    case 'show':
      return { kind: 'show' };
    case 'tap':
      return { kind: 'tap', squares: tapTargets(task.fen, task.rule) };
    case 'move':
      return { kind: 'move', moves: movesMeeting(task.fen, task.rule) };
    case 'yesno':
      return { kind: 'yesno', answer: yesNoTruth(task.fen, task.rule) };
    case 'choose': {
      const matching = chooseMatching(task.fen, task.rule, task.options);
      return { kind: 'choose', answer: matching.length === 1 ? (matching[0] as ChoiceN) : null, matching };
    }
    case 'teachme': {
      const after = tryApplyMove(task.fen, task.bad);
      const bad = after === null || !isSafeMove(task.fen, task.bad) || !meetsMoveRule(task.fen, task.bad, task.rule);
      return { kind: 'teachme', judge: bad ? 'no' : 'yes', moves: movesMeeting(task.fen, task.rule) };
    }
    case 'flags':
      return { kind: 'flags', from: task.from, squares: [...task.flags] };
  }
}

// ───────────────────────── judging one answer ─────────────────────────

export type TapVerdict =
  /** a target */
  | { ok: true; square: Square }
  /**
   * input, not a mistake: an own piece (the start of a move), an empty square (a finger slip), or in «что он хочет?»
   * a piece of HIS (the runner redirects: ours are asked)
   */
  | { ok: false; input: true; reason: 'notOurs' | 'hisPiece' | 'empty' }
  /** a wrong piece by meaning */
  | { ok: false; input: false; reason: MistakeReason };

/** Judges a touch of `sq` in a tap task (`targets` = `answerOf(task).squares`, pass it to avoid recomputing). */
export function checkTap(task: Extract<TaskSpec, { kind: 'tap' }>, sq: Square, targets?: Square[]): TapVerdict {
  const t = targets ?? tapTargets(task.fen, task.rule);
  if (t.includes(sq)) return { ok: true, square: sq };
  const piece = pieceAt(task.fen, sq);
  if (!piece) return { ok: false, input: true, reason: 'empty' };
  // the gift / reaches rules ask for pieces of the other side: a piece of the asking side is input (an own piece is
  // the start of a move; in «что он хочет?» — a reaches tap from his piece or GEN's `threat` — his pieces are the
  // ones that reach: 'hisPiece', the runner redirects)
  const by = askingSide(task.fen, task.rule);
  if (by !== null && piece.color === by) return { ok: false, input: true, reason: by === sideToMove(task.fen) ? 'notOurs' : 'hisPiece' };
  return { ok: false, input: false, reason: classifyMistake(task, sq) };
}

/**
 * The side whose pieces do the reaching in a gift / reaches / threat tap (its own pieces are never answers): the side
 * to move, or for `reaches` / `threat` the side of the piece on `from` — the opponent's piece in «что он хочет?»
 * (chapter 1.6, GEN's oppIdea@1). Null: any.
 */
function askingSide(fen: string, rule: TapRule): Color | null {
  if (rule.rule === 'gift') return sideToMove(fen);
  if (rule.rule === 'reaches') return pieceAt(fen, rule.from)?.color ?? sideToMove(fen);
  // GEN's «что он хочет?» (oppIdea@1): his moved piece on `from` does the reaching, our pieces are the answers
  if (rule.rule === 'threat') return pieceAt(fen, rule.from)?.color ?? null;
  return null;
}

/** The tap a move stands for in a tap task: «коснись» accepts taking the target too (§3.1 p.7). */
export function tapOfMove(fen: string, uci: string): Square | null {
  return captureOf(fen, uci)?.square ?? null;
}

export type MoveVerdict = { legal: false } | { legal: true; ok: boolean; uci: string };

/** Judges a move in a move task (any legal move meeting the rule is right). */
export function checkMove(task: Extract<TaskSpec, { kind: 'move' | 'teachme' }>, uci: string): MoveVerdict {
  const norm = normalizeUci(task.fen, uci);
  if (norm === null) return { legal: false };
  return { legal: true, ok: meetsMoveRule(task.fen, norm, task.rule), uci: norm };
}

export function checkYesNo(task: Extract<TaskSpec, { kind: 'yesno' }>, answer: YesNo): boolean {
  return yesNoTruth(task.fen, task.rule) === answer;
}

export function checkChoose(task: Extract<TaskSpec, { kind: 'choose' }>, n: ChoiceN): boolean {
  const truth = answerOf(task);
  return truth.kind === 'choose' && truth.answer === n;
}

// ───────────────────────── why it is wrong (§6.3) ─────────────────────────

/** Reason a capture of the piece on `sq` by the side to move is not a gift (or 'notReach' when it cannot capture). */
function captureReason(fen: string, sq: Square, by?: Square): MistakeReason {
  const board = parsePlacement(fen);
  const us = sideToMove(fen);
  // a capture of `sq`: a legal move onto an enemy piece there, or en passant onto the square behind it
  const caps = legalMovesUci(fen).filter((m) => (by ? m.startsWith(by) : true) && takenOn(board, m) === sq && board[squareIndex(sq)] !== null && board[squareIndex(sq)]?.color !== us);
  if (caps.length === 0) {
    // only our king reaches it, and it may not take a guarded piece
    const idx = squareIndex(sq);
    const att = attackersOf(board, idx, sideToMove(fen)).filter((a) => !by || squareName(a) === by);
    return att.length > 0 && att.every((a) => board[a]?.type === 'k') ? 'kingCantTake' : 'notReach';
  }
  const gs = guards(fen, sq);
  const guardPieces = gs.map((g) => board[squareIndex(g)] as BoardPiece);
  const kingOnly = caps.every((m) => pieceAt(fen, m.slice(0, 2))?.type === 'k');
  if (gs.length > 0) {
    if (kingOnly) return 'kingCantTake';
    return guardPieces.some((p) => p.type === 'p') ? 'guardedByPawn' : 'guarded';
  }
  // capturable, unguarded, still not a gift: poisoned — we lose material back
  return 'weGiveToo';
}

/**
 * The king step `uci` left an own piece it guarded as a gift (ch. 1.5 «Шах!»): the opponent, to move in `after`, has
 * an `isGift` capture on a square next to the king before the step and not next to it after.
 */
function kingLeftGuard(fen: string, uci: string, after: string): boolean {
  if (pieceAt(fen, uci.slice(0, 2))?.type !== 'k') return false;
  const from = squareIndex(uci.slice(0, 2));
  const to = squareIndex(uci.slice(2, 4));
  const near = (a: number, b: number): boolean => Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b))) === 1;
  const board = parsePlacement(after);
  return giftMoves(after).some((m) => {
    const sq = squareIndex(takenOn(board, m));
    return near(from, sq) && !near(to, sq);
  });
}

/**
 * What a wrong move loses (one rule for an escape and a «найди безопасный ход» answer, ch. 1.3 / 1.5): a piece the king
 * stepped away from «король ушёл — фигуру некому охранять» (leftUnguarded); the moved piece where it went «сюда
 * нельзя» (willBeTaken); another piece of ours «после такого хода нашу фигуру заберут» (notSaved).
 */
function lossReason(fen: string, uci: string, after: string): MistakeReason {
  if (kingLeftGuard(fen, uci, after)) return 'leftUnguarded';
  return pieceLost(after, uci.slice(2, 4)) ? 'willBeTaken' : 'notSaved';
}

/** Reason of a played (legal) move that missed `rule`. */
function moveReason(fen: string, uci: string, rule: MoveRule): MistakeReason {
  const after = tryApplyMove(fen, uci);
  if (after === null) return 'other';
  const moved = uci.slice(2, 4);
  switch (rule.rule) {
    case 'takesGift': {
      const cap = captureOf(fen, uci);
      if (!cap) return pieceLost(after, moved) ? 'willBeTaken' : 'other';
      const mover = pieceAt(fen, uci.slice(0, 2));
      const gs = guards(fen, cap.square);
      if (mover?.type === 'k' && gs.length > 0) return 'kingCantTake';
      const board = parsePlacement(fen);
      if (gs.some((g) => (board[squareIndex(g)] as BoardPiece).type === 'p')) return 'guardedByPawn';
      return 'weGiveToo';
    }
    case 'mate':
      return isCheck(after) ? 'notMate' : 'notCheck';
    case 'safeCheck':
      // a check that loses something says WHAT it loses (the one rule of `uci` / `escapesCheck`): the checking piece
      // taken where it went «сюда нельзя», another piece of ours «после такого хода нашу фигуру заберут»
      return isCheck(after) ? lossReason(fen, uci, after) : 'notCheck';
    case 'saves':
      // «сюда нельзя» only when the moved piece itself is taken where it went; else the piece «под ударом» still is, or
      // (v1-H) the move drops another piece of ours — the attacked piece that ran off its guard duty too: notSaved
      return pieceLost(after, moved) ? 'willBeTaken' : 'notSaved';
    case 'escapesCheck':
      // the same reasons as a `uci` escape's (ch. 1.5): what the answer loses
      return lossReason(fen, uci, after);
    case 'goodTrade':
      // a capture that is not a good trade gives more than it takes; a quiet move misses the trade (or drops the piece)
      if (captureOf(fen, uci)) return 'badTrade';
      return pieceLost(after, moved) ? 'willBeTaken' : 'fairTrade';
    case 'uci':
      // whichever piece moved: a wrong move that leaves something of ours as a gift says what it loses (`lossReason`);
      // one that loses nothing is just not the answer
      if (isSafeMove(fen, uci)) return 'other';
      return lossReason(fen, uci, after);
  }
}

/**
 * Why our piece on `sq` is not won by the opponent piece on `from` (a «что он хочет?» tap / a false «yes») — told from
 * HIS side: the lines of «мы берём» (guarded «возьмём — заберут нашу», notReach «мы не достаём») would be false here.
 */
function threatReason(fen: string, from: Square, sq: Square): MistakeReason {
  // his piece does not reach ours: «эту нашу фигуру он не достаёт» (chapter 1.6's notThreat, not «мы не достаём»)
  if (!reaches(fen, from).includes(sq)) return 'notThreat';
  // he reaches it, but we take back: «нашу фигуру охраняют — даром он её не заберёт»; unguarded and still no gift for
  // him: taking it loses him material (poisoned for him)
  return guards(fen, sq).length > 0 ? 'threatGuarded' : 'threatNotFree';
}

/** Reason the dashed `move` of a yes/no or choose task is not what the rule asks (the child said «yes» to it). */
function falseYesReason(fen: string, rule: YesNoRule): MistakeReason {
  if (rule.rule === 'threat') {
    // nothing is won: name what the moved piece reaches (guarded) or that it reaches nothing of ours
    const from = rule.move.slice(2, 4);
    const own = reaches(fen, from, { targets: true });
    return own.length > 0 ? threatReason(fen, from, own[0] as Square) : 'threatNone';
  }
  if (!isLegalMove(fen, rule.move)) return 'notReach';
  switch (rule.rule) {
    case 'isGift': {
      const cap = captureOf(fen, rule.move);
      if (!cap) return 'notReach';
      return captureReason(fen, cap.square, rule.move.slice(0, 2));
    }
    case 'isSafe': {
      // the moved piece is the gift («сюда нельзя»), or the move leaves another piece to be taken
      const after = tryApplyMove(fen, rule.move);
      return after === null || pieceGiven(after, rule.move.slice(2, 4)) ? 'willBeTaken' : 'notSaved';
    }
    case 'isMate': {
      const after = tryApplyMove(fen, rule.move);
      return after !== null && isCheck(after) ? 'notMate' : 'notCheck';
    }
    case 'goodTrade':
      return captureOf(fen, rule.move) ? 'badTrade' : 'notReach';
  }
}

/**
 * Why `answer` is wrong (§6.3): a square for tap, a UCI move for move / teachme, 'yes' | 'no' for yesno and the
 * teachme judgement, an option number (1-based, number or string) for choose. `task.why[answer]` wins.
 * Touching an own piece in a gift / reaches tap task is 'notOurs' (input, not counted) — in «что он хочет?» (reaches
 * from an opponent piece) touching his piece is 'hisPiece' (input too), and a piece of ours his piece does not reach is
 * 'notThreat'. A 👍 on Гамбитик's teachme move: 'willBeTaken' when the move is not safe, else why it misses the rule
 * (a save that leaves the attacked piece behind: 'notSaved').
 */
export function classifyMistake(task: TaskSpec, answer: string | number): MistakeReason {
  const key = String(answer);
  const authored = task.why?.[key];
  if (authored) return authored;
  const fen = task.fen;
  switch (task.kind) {
    case 'tap': {
      const piece = pieceAt(fen, key);
      if (!piece) return 'other';
      const rule = task.rule;
      if (rule.rule === 'gift') {
        if (piece.color === sideToMove(fen)) return 'notOurs';
        return captureReason(fen, key);
      }
      if (rule.rule === 'reaches') {
        const us = sideToMove(fen);
        const by = askingSide(fen, rule);
        if (piece.color === by) return by === us ? 'notOurs' : 'hisPiece';
        // «что он хочет?» (reaches from his piece): a piece of ours his piece does not reach
        if (by !== us) return 'notThreat';
        // «Сюда никто из наших не достаёт.» is false when another own piece reaches it: say «эта не достаёт» then
        const board = parsePlacement(fen);
        const other = board.some((p, i) => p !== null && p.color === us && squareName(i) !== rule.from && reaches(fen, squareName(i)).includes(key));
        return other ? 'notReachThis' : 'notReach';
      }
      if (rule.rule === 'guards') return 'notGuard';
      if (rule.rule === 'threat') return piece.color === sideToMove(fen) ? threatReason(fen, rule.from, key) : 'hisPiece';
      return 'other';
    }
    case 'move': {
      const norm = normalizeUci(fen, key);
      return norm === null ? 'other' : moveReason(fen, norm, task.rule);
    }
    case 'teachme': {
      if (key === 'yes' || key === 'no') {
        if (!isLegalMove(fen, task.bad)) return 'other';
        return isSafeMove(fen, task.bad) ? moveReason(fen, task.bad, task.rule) : 'willBeTaken';
      }
      const norm = normalizeUci(fen, key);
      return norm === null ? 'other' : moveReason(fen, norm, task.rule);
    }
    case 'yesno': {
      const truth = yesNoTruth(fen, task.rule);
      // said «no» to a real gift: «это подарок — его никто не охраняет»; to a fair trade: «забираем не дешевле» (a
      // gift there is still 'missedGift'); a real threat; a safe move: «даром ничего не заберут»; a mate: no reason line
      if (truth === 'yes') {
        if (task.rule.rule === 'goodTrade') return isGift(fen, task.rule.move) ? 'missedGift' : 'fairTrade';
        const missed: Partial<Record<YesNoRule['rule'], MistakeReason>> = { isGift: 'missedGift', threat: 'threatMissed', isSafe: 'missedSafe' };
        return missed[task.rule.rule] ?? 'other';
      }
      return falseYesReason(fen, task.rule);
    }
    case 'choose': {
      const move = task.options[Number(key) - 1];
      if (!move) return 'other';
      if (task.rule.rule === 'mostValuable') return 'cheaper';
      return falseYesReason(fen, { rule: task.rule.rule, move });
    }
    case 'flags': {
      const to = key.slice(2, 4);
      const board = parsePlacement(fen);
      return board.some((p, i) => p && p.color !== sideToMove(fen) && reaches(fen, squareName(i)).includes(to)) ? 'willBeTaken' : 'other';
    }
    case 'show':
      return 'other';
  }
}

// ───────────────────────── the consequence (red reply arrow) ─────────────────────────

/** Reasons that say ANOTHER piece of ours is lost, not the moved one where it went (the arrow must agree, v1-H). */
const OTHER_PIECE_REASONS: ReadonlySet<MistakeReason> = new Set<MistakeReason>(['notSaved', 'leftUnguarded']);

/**
 * The opponent's reply that shows why `uci` is bad — the same loss the reason line names (`lossReason`, v1-H):
 * - with `reason` allowsMate (a riddle of the child's own game) the mate in one the move allows;
 * - the moved piece is lost where it went (`pieceLost`: «сюда нельзя», willBeTaken) → the cheapest capture of it;
 * - else the biggest gift capture the move leaves, else the capture that wins the most in an exchange (a piece we
 *   lose though we take back — `saves` counts that loss too);
 * - else an even exchange on the landing square (a recapture we answer), else after a check that is not mate a king
 *   step out. Null = nothing to show.
 * With `reason` notSaved / leftUnguarded («нашу фигуру заберут» — another piece) the moved piece's capture is never
 * the arrow while another piece is lost: a pawn step that leaves the rook «под ударом» shows the rook taken, not the
 * pawn exchanged.
 */
export function consequenceOf(fen: string, uci: string, reason?: MistakeReason): string | null {
  const norm = normalizeUci(fen, uci);
  if (norm === null) return null;
  const after = tryApplyMove(fen, norm);
  if (after === null) return null;
  const replies = legalMovesUci(after);
  const board = parsePlacement(after);
  const valueAt = (sq: string): number => {
    const p = board[squareIndex(sq)];
    return p ? VALUE_PAWNS[p.type] || 100 : 0;
  };
  const to = norm.slice(2, 4);
  // «после этого хода нам ставят мат» (a riddle of the child's own game, review round 3): the mate itself
  if (reason === 'allowsMate') {
    const mate = replies.find((r) => {
      const next = tryApplyMove(after, r);
      return next !== null && isMate(next);
    });
    if (mate !== undefined) return mate;
  }
  const other = reason !== undefined && OTHER_PIECE_REASONS.has(reason);
  // the square a reply takes on — en passant takes the double-stepped pawn from ITS square, not the landing one (read off
  // the board: a pawn stepping sideways onto an empty square; `captureOf` per reply made this a move generation per
  // reply — the generator's gates call it on every board they try)
  const takes = (m: string): string => takenOn(board, m);
  const recaptures = replies.filter((m) => takes(m) === to).sort((a, b) => valueAt(a.slice(0, 2)) - valueAt(b.slice(0, 2)));
  if (recaptures.length > 0 && !other && pieceLost(after, to)) return recaptures[0] as string;
  const gifts = giftMoves(after)
    .filter((m) => !other || takes(m) !== to)
    .sort((a, b) => valueAt(takes(b)) - valueAt(takes(a)));
  if (gifts.length > 0) return gifts[0] as string;
  // a losing exchange it leaves: the capture winning the most by SEE (a pawn on a guarded knight, a rook on the queen)
  let won: string | null = null;
  let gain = 0;
  for (const m of replies) {
    if (other && takes(m) === to) continue;
    const g = tradeBalance(after, m);
    if (g !== null && g > gain) {
      gain = g;
      won = m;
    }
  }
  if (won) return won;
  if (recaptures.length > 0) return recaptures[0] as string;
  if (isCheck(after)) {
    const kingSteps = replies.filter((m) => pieceAt(after, m.slice(0, 2))?.type === 'k');
    return kingSteps[0] ?? replies[0] ?? null;
  }
  return null;
}

// ───────────────────────── content truth (path:report) ─────────────────────────

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

/** Truth problems of one authored task; [] = fine. */
export function verifyTask(task: TaskSpec): string[] {
  const out: string[] = [];
  const at = `${task.key}:`;
  const posProblem = positionProblem(task.fen, task.kingless);
  if (posProblem) return [`${at} ${posProblem}`];
  const legal = (uci: string, fen = task.fen): boolean => isLegalMove(fen, uci);
  switch (task.kind) {
    case 'show': {
      let fen = task.fen;
      for (const [i, beat] of task.beats.entries()) {
        for (const m of [beat.move, beat.reply]) {
          if (!m) continue;
          const next = tryApplyMove(fen, m);
          if (next === null) {
            out.push(`${at} beat ${i + 1}: illegal move ${m}`);
            break;
          }
          fen = next;
        }
      }
      break;
    }
    case 'tap': {
      const squares = tapTargets(task.fen, task.rule);
      if (squares.length === 0) out.push(`${at} no answer: the rule ${task.rule.rule} gives no square`);
      if (task.expect && !sameSet(task.expect, squares)) out.push(`${at} expect [${task.expect.join(',')}] ≠ rule [${squares.join(',')}]`);
      break;
    }
    case 'move': {
      if (task.rule.rule === 'uci') for (const m of task.rule.moves) if (!legal(m)) out.push(`${at} illegal move ${m} in the rule`);
      const moves = movesMeeting(task.fen, task.rule);
      if (moves.length === 0) out.push(`${at} no answer: no legal move meets ${task.rule.rule}`);
      if (task.expect) {
        for (const m of task.expect) {
          const norm = normalizeUci(task.fen, m);
          if (norm === null) out.push(`${at} expect ${m} is illegal`);
          else if (!moves.includes(norm)) out.push(`${at} expect ${m} does not meet ${task.rule.rule}`);
        }
      }
      if (task.reply) {
        const first = task.expect?.[0] ?? moves[0];
        const after = first ? tryApplyMove(task.fen, first) : null;
        if (after && !legal(task.reply, after)) out.push(`${at} reply ${task.reply} is illegal after ${first}`);
      }
      break;
    }
    case 'yesno': {
      if (task.rule.rule === 'threat') {
        const to = task.rule.move.slice(2, 4);
        const p = pieceAt(task.fen, to);
        if (!p || p.color === sideToMove(task.fen)) out.push(`${at} the last move ${task.rule.move}: no opponent piece on ${to}`);
      } else if (!legal(task.rule.move)) out.push(`${at} the dashed move ${task.rule.move} is illegal`);
      const truth = yesNoTruth(task.fen, task.rule);
      if (task.expect && task.expect !== truth) out.push(`${at} expect ${task.expect} ≠ rule ${truth}`);
      break;
    }
    case 'choose': {
      if (task.options.length < 2 || task.options.length > 3) out.push(`${at} choose needs 2–3 options`);
      for (const m of task.options) if (!legal(m)) out.push(`${at} option ${m} is illegal`);
      const truth = answerOf(task);
      if (truth.kind === 'choose') {
        if (truth.matching.length === 0) out.push(`${at} no answer: no option meets ${task.rule.rule}`);
        if (truth.matching.length > 1) out.push(`${at} multiple answers: options ${truth.matching.join(',')} meet ${task.rule.rule}`);
        if (task.expect && truth.answer !== null && task.expect !== truth.answer) out.push(`${at} expect ${task.expect} ≠ rule ${truth.answer}`);
      }
      break;
    }
    case 'teachme': {
      if (!legal(task.bad)) out.push(`${at} the bad move ${task.bad} is illegal`);
      const truth = answerOf(task);
      if (truth.kind === 'teachme') {
        if (truth.judge !== 'no') out.push(`${at} the bad move ${task.bad} is not bad`);
        if (truth.moves.length === 0) out.push(`${at} no answer: no legal move meets ${task.rule.rule}`);
      }
      break;
    }
    case 'flags': {
      const piece = pieceAt(task.fen, task.from);
      if (!piece || piece.color !== sideToMove(task.fen)) out.push(`${at} no piece of the side to move on ${task.from}`);
      if (task.flags.length === 0) out.push(`${at} no flags`);
      break;
    }
  }
  if (task.why) {
    for (const k of Object.keys(task.why)) {
      if (task.kind === 'tap' && tapTargets(task.fen, task.rule).includes(k)) out.push(`${at} why names the right answer ${k}`);
    }
  }
  // a trade task: nothing else on the board may turn a trade the child counts on its square
  for (const uci of tradeQuestions(task)) {
    const problem = tradeProblem(task.fen, uci);
    if (problem) out.push(`${at} ${problem}`);
  }
  return out;
}

/** The legal captures a trade task judges by `tradeBalance`: the dashed move, the arrows, every capture of a move. */
function tradeQuestions(task: TaskSpec): string[] {
  const capture = (m: string): boolean => isLegalMove(task.fen, m) && captureOf(task.fen, m) !== null;
  if (task.kind === 'yesno' && task.rule.rule === 'goodTrade') return [task.rule.move].filter(capture);
  if (task.kind === 'choose' && task.rule.rule === 'goodTrade') return task.options.filter(capture);
  if (task.kind === 'move' && task.rule.rule === 'goodTrade') return legalMovesUci(task.fen).filter(capture);
  return [];
}
